import { useEffect, useRef, useState } from 'react'
import { Stack, Tooltip, Typography, useTheme } from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import Button from '@mui/material/Button'
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded'
import Box from '@mui/material/Box'
import { useNavigate } from 'react-router-dom'
import { InfoOutlineRounded } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { PDFViewer } from '@react-pdf/renderer'
import { getIsDemoAccount } from '../../utils'
import { DiversityDimenstionConfig, Module, ModuleConfig } from '../../shared/model'
import { GapExtended } from '../../shared/model/models'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { setCWIMappings, setOFAOverall, setOFAValues } from '../../store/actions'
import { downloadOFAGuide } from './fs-ofa-guide'
import { getFormattedString } from '../../shared/utils'
import FsDemoAccountModal from '../common/molecules/DemoAccountModal'
import FsAlert, { FsAlertRef } from '../common/atoms/Alert'
import FsFullPageLoader from '../common/atoms/FullPageLoader'
import LoadingDash from '../common/atoms/LoadingDash'

type FsOptionsForActionsProps = {
  latestGap: GapExtended
  contextModelVariantId: number
  moduleConfig?: ModuleConfig
  diversityDimenstionConfig?: DiversityDimenstionConfig
}

const FsOptionsForActions = ({
  latestGap,
  contextModelVariantId,
  moduleConfig,
  diversityDimenstionConfig,
}: FsOptionsForActionsProps) => {
  const { t, i18n } = useTranslation()
  const { currentRun, ofaValues, ofaOverall, ofaTrainingCost, cwiMappings } = useAppSelector(
    (state) => state.currentAnalysis,
  )
  const dispatch = useAppDispatch()
  const [downloading, setDownloading] = useState<boolean>(false)
  const [openDemoModal, setOpenDemoModal] = useState<boolean>(false)
  const theme = useTheme()
  const navigate = useNavigate()
  const alertRef = useRef<FsAlertRef>(null)

  const isDemoAccount = getIsDemoAccount()
  const showOFASimulator = true

  useEffect(() => {
    if (
      showOFASimulator &&
      currentRun &&
      moduleConfig?.module &&
      (!ofaValues || !ofaValues[contextModelVariantId])
    ) {
      dispatch(
        setOFAValues({
          contextModelVariantId,
          runId: currentRun.id,
          module: moduleConfig?.module ?? '',
        }),
      )
    }
  }, [contextModelVariantId, currentRun, showOFASimulator, moduleConfig])

  useEffect(() => {
    if (currentRun && (!cwiMappings || !cwiMappings[currentRun.id])) {
      dispatch(setCWIMappings({ runId: currentRun.id }))
    }
  }, [currentRun])

  useEffect(() => {
    if (
      showOFASimulator &&
      currentRun &&
      ofaValues &&
      ofaValues[contextModelVariantId] &&
      (!ofaOverall || !ofaOverall[contextModelVariantId])
    ) {
      dispatch(setOFAOverall({ contextModelVariantId }))
    }
  }, [contextModelVariantId, currentRun, ofaValues, showOFASimulator])

  const downloadGuide = async () => {
    if (isDemoAccount) {
      setOpenDemoModal(true)
      return
    }
    if (
      currentRun &&
      ofaValues &&
      ofaValues[contextModelVariantId] &&
      ofaOverall &&
      ofaOverall[contextModelVariantId] &&
      cwiMappings &&
      cwiMappings[currentRun.id] &&
      (moduleConfig?.module !== Module.TRAINING || !!ofaTrainingCost)
    ) {
      try {
        setDownloading(true)
        await downloadOFAGuide(
          moduleConfig?.module === Module.CAREER
            ? ofaValues[contextModelVariantId]
                .filter((v) => v.isCurrentOption)
                .sort((a, b) => (a.careerLevel ?? 0) - (b.careerLevel ?? 0))
            : ofaValues[contextModelVariantId]
                .filter((v) => v.isCurrentOption)
                .sort(
                  (a, b) => parseInt(a.ofaVariableLabel, 10) - parseInt(b.ofaVariableLabel, 10),
                ),
          ofaOverall[contextModelVariantId],
          latestGap.description.groupOfInterest.groupVariable.label,
          latestGap.description.referenceGroup.groupVariable.label,
          t,
          i18n,
          currentRun.runAt,
          cwiMappings[currentRun.id],
          moduleConfig,
          diversityDimenstionConfig,
          moduleConfig?.module === Module.TRAINING ? ofaTrainingCost : undefined,
        )
      } catch {
        alertRef.current?.openAlert({ severity: 'error', message: t('something.went.wrong') })
      } finally {
        setDownloading(false)
      }
    }
  }

  // const Template =
  //   currentRun &&
  //   ofaValues &&
  //   ofaValues[contextModelVariantId] &&
  //   ofaOverall &&
  //   ofaOverall[contextModelVariantId]
  //     ? downloadOFAGuide(
  //         ofaValues[contextModelVariantId]
  //           .filter((v) => v.isCurrentOption)
  //           .sort((a, b) => a.ofaVariable - b.ofaVariable),
  //         ofaOverall[contextModelVariantId],
  //         gap.description.groupOfInterest.groupVariable.label,
  //         gap.description.referenceGroup.groupVariable.label,
  //         t,
  //         i18n,
  //         currentRun.runAt,
  //         moduleConfig,
  //         diversityDimenstionConfig,
  //       )
  //     : undefined

  return (
    <Box mt={4} className="ofa_walkthrough h-full">
      <FsDemoAccountModal isOpen={openDemoModal} setIsOpen={setOpenDemoModal} />
      {moduleConfig ? (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mt={-4.75}>
            <Typography variant="h2" className="font-semibold">{`${t(
              'options.for.action',
            )}`}</Typography>
            <Tooltip
              title={
                <Box p={1} px={2}>
                  <Typography>
                    {t('ofa.intro', { module: t(`module.name.${moduleConfig?.module}`) })}
                  </Typography>
                </Box>
              }
              placement="right"
              arrow
              enterTouchDelay={0}
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: 500,
                  },
                },
              }}
            >
              <InfoOutlineRounded
                fontSize="small"
                sx={{ ml: 1, color: theme.palette.text.secondary }}
              />
            </Tooltip>
          </Stack>
          {ofaOverall && ofaOverall[contextModelVariantId] ? (
            <Stack className="justify-between h-full pb-6">
              <Stack className="flex-row justify-around items-center mt-12">
                <Box className="text-center">
                  <Typography variant="h3">{t('goal')}</Typography>
                  <Typography variant="n2" mt={2}>
                    {getFormattedString(
                      i18n.language,
                      `${ofaOverall[contextModelVariantId].overallGap}`,
                      1,
                      1,
                    )}{' '}
                    %
                  </Typography>
                  <Typography>{t(`${moduleConfig?.module}_gap`)}</Typography>
                </Box>
                <Box className="text-center">
                  <Typography variant="h3">{t('ofa.costs')}</Typography>
                  <Typography variant="n2" mt={2}>
                    {getFormattedString(
                      i18n.language,
                      `${ofaOverall[contextModelVariantId].additionalCost}`,
                      2,
                      2,
                      true,
                    )}
                    {' \u20ac'}
                  </Typography>
                  <Typography>{t('ofa.per.month')}</Typography>
                </Box>
              </Stack>

              <Stack direction="row" justifyContent="center">
                <Button
                  variant="contained"
                  startIcon={<TuneIcon />}
                  onClick={() => {
                    if (isDemoAccount) setOpenDemoModal(true)
                    else navigate(`/fs/ofa/simulator/${latestGap?.contextModelVariantId}`)
                  }}
                >
                  {t('ofa.open.interactive')}
                </Button>
                <Button
                  variant="outlined"
                  sx={{ ml: 2 }}
                  onClick={downloadGuide}
                  startIcon={<FileDownloadRoundedIcon />}
                >
                  {t('ofa.download.guide.button')}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Box mt={4.25}>
              <LoadingDash title={t('loading')} />
            </Box>
          )}
        </>
      ) : null}

      {/* <PDFViewer width={1300} height={1500}>
        {Template}
      </PDFViewer> */}
      {downloading ? <FsFullPageLoader /> : null}
      <FsAlert ref={alertRef} />
    </Box>
  )
}

export default FsOptionsForActions
